import { schoolYears } from '../../utils/schoolYears';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { useAuth } from '../../context/AuthContext.js';

const validYears = schoolYears();

function EditGroup() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');
  const { userInfo } = useAuth();
  const userRole = userInfo?.role || '';

  const [groupName, setGroupName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [schoolYear, setSchoolYear] = useState('');
  const [locId, setLocId] = useState('');

  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [addedStudents, setAddedStudents] = useState([]);
  const [addedTeachers, setAddedTeachers] = useState([]);

  const [selectedStudentOptions, setSelectedStudentOptions] = useState([]);
  const [selectedTeacherOptions, setSelectedTeacherOptions] = useState([]);

  const [addedStudentSearchTerm, setAddedStudentSearchTerm] = useState('');
  const [addedTeacherSearchTerm, setAddedTeacherSearchTerm] = useState('');

  const [loadingAddStudent, setLoadingAddStudent] = useState(false);
  const [studentError, setStudentError] = useState(null);
  const [studentSuccess, setStudentSuccess] = useState(null);

  const [loadingAddTeacher, setLoadingAddTeacher] = useState(false);
  const [teacherError, setTeacherError] = useState(null);
  const [teacherSuccess, setTeacherSuccess] = useState(null);

  const [nameSaveError, setNameSaveError] = useState(null);
  const [nameSaveSuccess, setNameSaveSuccess] = useState(null);

  const [yearSaveError, setYearSaveError] = useState(null);
  const [yearSaveSuccess, setYearSaveSuccess] = useState(null);

  const [locations, setLocations] = useState([]);
  const [locationSaveError, setLocationSaveError] = useState(null);
  const [locationSaveSuccess, setLocationSaveSuccess] = useState(null);

  const sortByName = (a, b) => {
    const nameA = `${a.firstname || ''} ${a.lastname || ''}`.toLowerCase();
    const nameB = `${b.firstname || ''} ${b.lastname || ''}`.toLowerCase();
    return nameA.localeCompare(nameB);
  };

  const filterBySearchTerm = (person, searchTerm) => {
    if (!searchTerm) return true;
    const fullName = `${person.firstname || ''} ${person.lastname || ''}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  };

  const getFullName = (person) => `${person.firstname || ''} ${person.lastname || ''}`.trim();

  const hasMatchingTeacherLocation = (teacher, groupLocationId) => {
    if (!groupLocationId) return true;

    if (!Array.isArray(teacher?.location_ids)) return false;

    return teacher.location_ids.some(
      (teacherLocId) => String(teacherLocId).trim() === String(groupLocationId).trim()
    );
  };

  useEffect(() => {
    axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/group/get/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then(res => {
        const data = res.data?.data || res.data;

        setGroupName(data?.groep_name || '');
        setOriginalName(data?.groep_name || '');
        setSchoolYear(data?.school_year || '');
        setLocId(data?.location_id ? String(data.location_id).trim() : '');
        setAddedStudents((data?.students || []).sort(sortByName));
        setAddedTeachers((data?.docenten || []).sort(sortByName));
      })
      .catch(err => console.error('Error fetching group data', err));
  }, [id, token]);

  useEffect(() => {
    axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/students/getstudentnamelist`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
      .then(res => {
        const active = res.data.Data || [];

        const filtered = active
          .filter(s => !addedStudents.some(as => as.student_id === s.student_id))
          .filter(s => {
            if (userRole === 'super_admin') {
              if (locId) {
                return String(s.location_id).trim() === String(locId).trim();
              }
              return true;
            }

            if (userRole === 'locatie_admin') {
              const ownLocId = userInfo?.user_location_ids?.[0];
              return String(s.location_id).trim() === String(ownLocId).trim();
            }

            return true;
          });

        setStudents(filtered.sort(sortByName));
      })
      .catch(err => console.error('Error fetching students', err));
  }, [addedStudents, locId, userInfo?.user_location_ids, userRole, token]);

  useEffect(() => {
    axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/docent/alldocent`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
      .then(res => {
        const active = res.data.Data || [];

        const filtered = active
          .filter(t => !addedTeachers.some(at => at.docent_id === t.docent_id))
          .filter(t => hasMatchingTeacherLocation(t, locId));

        setTeachers(filtered.sort(sortByName));
      })
      .catch(err => console.error('Error fetching teachers', err));
  }, [addedTeachers, locId, token]);

  useEffect(() => {
    axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/location/allLocations`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
      .then(res => {
        const rawLocations = res.data?.data || res.data || [];
        const normalizedLocations = Array.isArray(rawLocations)
          ? rawLocations.map(loc => ({
              ...loc,
              location_id: loc.location_id ? String(loc.location_id).trim() : ''
            }))
          : [];

        setLocations(normalizedLocations);
      })
      .catch(err => console.error('Error fetching locations', err));
  }, [token]);

  useEffect(() => {
    setSelectedStudentOptions(prev =>
      prev.filter(option =>
        students.some(s => s.student_id === option.value)
      )
    );
  }, [students]);

  useEffect(() => {
    setSelectedTeacherOptions(prev =>
      prev.filter(option =>
        teachers.some(t => t.docent_id === option.value)
      )
    );
  }, [teachers]);

  const handleAddStudents = async () => {
    if (selectedStudentOptions.length === 0) return;

    setStudentError(null);
    setStudentSuccess(null);
    setLoadingAddStudent(true);

    let successCount = 0;
    const failedNames = [];

    try {
      for (const option of selectedStudentOptions) {
        const payload = { groep_id: id, student_id: option.value };

        try {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/group/addstudent`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const newStudent = students.find(s => s.student_id === payload.student_id);
          if (newStudent) {
            setAddedStudents(prev => [...prev, newStudent].sort(sortByName));
          }

          setStudents(prev => prev.filter(s => s.student_id !== payload.student_id));
          successCount += 1;
        } catch (err) {
          failedNames.push(option.label);
        }
      }

      setSelectedStudentOptions([]);

      if (successCount > 0) {
        setStudentSuccess(
          successCount === 1
            ? '1 leerling succesvol toegevoegd.'
            : `${successCount} leerlingen succesvol toegevoegd.`
        );
      }

      if (failedNames.length > 0) {
        setStudentError(
          `Deze leerling(en) konden niet worden toegevoegd: ${failedNames.join(', ')}`
        );
      }
    } finally {
      setLoadingAddStudent(false);
    }
  };

  const handleAddTeachers = async () => {
    if (selectedTeacherOptions.length === 0) return;

    setTeacherError(null);
    setTeacherSuccess(null);
    setLoadingAddTeacher(true);

    let successCount = 0;
    const failedNames = [];

    try {
      for (const option of selectedTeacherOptions) {
        const payload = { groep_id: id, docent_id: option.value };

        try {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/group/adddocent`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const newTeacher = teachers.find(t => t.docent_id === payload.docent_id);
          if (newTeacher) {
            setAddedTeachers(prev => [...prev, newTeacher].sort(sortByName));
          }

          setTeachers(prev => prev.filter(t => t.docent_id !== payload.docent_id));
          successCount += 1;
        } catch (err) {
          failedNames.push(option.label);
        }
      }

      setSelectedTeacherOptions([]);

      if (successCount > 0) {
        setTeacherSuccess(
          successCount === 1
            ? '1 docent succesvol toegevoegd.'
            : `${successCount} docenten succesvol toegevoegd.`
        );
      }

      if (failedNames.length > 0) {
        setTeacherError(
          `Deze docent(en) konden niet worden toegevoegd: ${failedNames.join(', ')}`
        );
      }
    } finally {
      setLoadingAddTeacher(false);
    }
  };

  const handleRemoveStudent = async (studentId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/group/deletestudent`,
        {
          data: { groep_id: id.trim(), student_id: studentId },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const removed = addedStudents.find(s => s.student_id === studentId);
      setAddedStudents(prev => prev.filter(s => s.student_id !== studentId));

      if (removed) {
        const matchesLocation =
          userRole === 'super_admin'
            ? !locId || String(removed.location_id || '').trim() === String(locId).trim()
            : true;

        if (matchesLocation) {
          setStudents(prev => [...prev, removed].sort(sortByName));
        }
      }
    } catch (err) {
      console.error('Error removing student', err);
    }
  };

  const handleRemoveTeacher = async (teacherId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/group/deletedocent`,
        {
          data: { groep_id: id.trim(), docent_id: teacherId },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const removed = addedTeachers.find(t => t.docent_id === teacherId);
      setAddedTeachers(prev => prev.filter(t => t.docent_id !== teacherId));

      if (removed && hasMatchingTeacherLocation(removed, locId)) {
        setTeachers(prev => [...prev, removed].sort(sortByName));
      }
    } catch (err) {
      console.error('Error removing teacher', err);
    }
  };

  const handleGroupNameChange = (e) => {
    setGroupName(e.target.value);
  };

  const handleSchoolYearChange = (e) => {
    const val = e.target.value;
    setSchoolYear(val);
    setYearSaveError(null);
    setYearSaveSuccess(null);

    axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/group/edit/${id}`,
      {
        school_year: val,
        groep_name: groupName,
        location_id: locId
      },
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then(() => {
        setYearSaveSuccess('Schooljaar bijgewerkt');
        setTimeout(() => setYearSaveSuccess(null), 2000);
      })
      .catch(err => {
        console.error('Error updating school year', err);
        setYearSaveError('Fout bij bijwerken schooljaar');
        setTimeout(() => setYearSaveError(null), 2000);
      });
  };

  const handleLocationChange = (e) => {
    const newLocationId = String(e.target.value || '').trim();

    setLocationSaveError(null);
    setLocationSaveSuccess(null);

    axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/group/edit/${id}`,
      {
        groep_name: groupName,
        school_year: schoolYear,
        location_id: newLocationId
      },
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then(() => {
        setLocId(newLocationId);
        setSelectedStudentOptions([]);
        setSelectedTeacherOptions([]);
        setLocationSaveSuccess('Locatie bijgewerkt');
        setTimeout(() => setLocationSaveSuccess(null), 2000);
      })
      .catch(err => {
        console.error('Error updating location', err);
        setLocationSaveError(err.response?.data?.error || 'Fout bij bijwerken locatie');
        setTimeout(() => setLocationSaveError(null), 2500);
      });
  };

  const handleSaveName = async () => {
    setNameSaveError(null);
    setNameSaveSuccess(null);

    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/group/edit/${id}`,
        {
          groep_name: groupName,
          school_year: schoolYear,
          location_id: locId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setOriginalName(groupName);
      setIsEditingName(false);
      setNameSaveSuccess('Wijziging groepsnaam succesvol opgeslagen');
      setTimeout(() => setNameSaveSuccess(null), 2000);
    } catch (err) {
      console.error('Error saving group name', err);
      setNameSaveError('Fout bij opslaan groepsnaam');
      setTimeout(() => setNameSaveError(null), 2000);
    }
  };

  const filteredAddedStudents = addedStudents.filter(s =>
    filterBySearchTerm(s, addedStudentSearchTerm)
  );

  const filteredAddedTeachers = addedTeachers.filter(t =>
    filterBySearchTerm(t, addedTeacherSearchTerm)
  );

  const hasMatchingLocationOption = locations.some(
    l => String(l.location_id).trim() === String(locId || '').trim()
  );

  const studentOptions = students.map((student) => ({
    value: student.student_id,
    label: getFullName(student)
  }));

  const teacherOptions = teachers.map((teacher) => ({
    value: teacher.docent_id,
    label: getFullName(teacher)
  }));

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '44px',
      borderRadius: '8px',
      borderColor: state.isFocused ? '#0d6efd' : '#dee2e6',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(13,110,253,0.12)' : 'none',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      '&:hover': { borderColor: '#0d6efd' }
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#e8f0fe',
      borderRadius: '20px',
      padding: '0 4px'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#1a56db',
      fontSize: '13px',
      fontWeight: 500
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#1a56db',
      borderRadius: '0 20px 20px 0',
      cursor: 'pointer',
      '&:hover': { backgroundColor: '#c7d7fc', color: '#1a56db' }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#adb5bd',
      fontSize: '14px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#f0f4ff' : 'white',
      color: state.isSelected ? 'white' : '#212529',
      fontSize: '14px',
      cursor: 'pointer'
    })
  };

  return (
    <div className='centered-container bg-white p-4'>
      <h2>Bewerk Groep</h2>

      <form onSubmit={e => e.preventDefault()}>
        {!userInfo?.is_single_user && (
          <div className='mb-3'>
            <label>Locatie</label>
            <select
              value={locId || ''}
              onChange={handleLocationChange}
              className='form-select'
            >
              <option value=''>-- Kies locatie --</option>
              {locations.map(location => (
                <option
                  key={location.location_id}
                  value={String(location.location_id).trim()}
                >
                  {location.location_name}
                </option>
              ))}
            </select>

            {locId && locations.length > 0 && !hasMatchingLocationOption && (
              <div className='text-danger mt-2'>
                Huidige locatie komt niet voor in de opgehaalde locatieslijst.
              </div>
            )}

            {locationSaveError && (
              <div className='alert alert-danger mt-2'>{locationSaveError}</div>
            )}
            {locationSaveSuccess && (
              <div className='alert alert-success mt-2'>{locationSaveSuccess}</div>
            )}
          </div>
        )}

        <div className='form-group mb-3'>
          <label>
            Schooljaar<span style={{ color: 'red' }}>*</span>
          </label>
          <select
            value={schoolYear}
            onChange={handleSchoolYearChange}
            className='form-select mb-1'
            required
          >
            <option value=''>-- Kies schooljaar --</option>
            {validYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          {yearSaveError && <div className='alert alert-danger'>{yearSaveError}</div>}
          {yearSaveSuccess && <div className='alert alert-success'>{yearSaveSuccess}</div>}
        </div>

        <div className='form-group mb-3'>
          <label>Groepsnaam</label>
          <div className='d-flex align-items-center'>
            <input
              type='text'
              value={groupName}
              onChange={handleGroupNameChange}
              disabled={!isEditingName}
              className='form-control me-2'
            />
            {isEditingName ? (
              <button
                type='button'
                className='btn btn-secondary'
                onClick={handleSaveName}
                disabled={groupName === originalName}
              >
                <i className="bi bi-check"></i>
              </button>
            ) : (
              <button
                type='button'
                onClick={() => setIsEditingName(true)}
                className='btn btn-secondary'
              >
                <i className="bi bi-pencil"></i>
              </button>
            )}
          </div>
          {nameSaveError && <div className='alert alert-danger'>{nameSaveError}</div>}
          {nameSaveSuccess && <div className='alert alert-success'>{nameSaveSuccess}</div>}
        </div>

        <div className='form-group mb-4'>
          <label>Leerlingen toevoegen</label>
          {studentError && <div className="alert alert-danger mt-2">{studentError}</div>}
          {studentSuccess && <div className="alert alert-success mt-2">{studentSuccess}</div>}

          <div className='mt-2'>
            <Select
              isMulti
              options={studentOptions}
              value={selectedStudentOptions}
              onChange={(selected) => setSelectedStudentOptions(selected || [])}
              placeholder='Zoek en selecteer leerling(en)...'
              styles={selectStyles}
              noOptionsMessage={() => 'Geen leerlingen gevonden'}
            />
          </div>

          <div className='mt-2 d-grid'>
            <button
              type="button"
              onClick={handleAddStudents}
              className='btn btn-primary'
              disabled={selectedStudentOptions.length === 0 || loadingAddStudent}
            >
              {loadingAddStudent
                ? 'Bezig met toevoegen...'
                : `Voeg geselecteerde leerling${selectedStudentOptions.length > 1 ? 'en' : ''} toe`}
            </button>
          </div>

          {students.length === 0 && (
            <small className='text-muted mt-2 d-block'>
              Geen beschikbare leerlingen voor deze locatie.
            </small>
          )}
        </div>

        <div className='mb-4'>
          <label>Toegevoegde Leerlingen ({addedStudents.length})</label>
          {addedStudents.length > 5 && (
            <div className='mb-2'>
              <input
                type='text'
                placeholder='🔍 Filter toegevoegde leerlingen...'
                value={addedStudentSearchTerm}
                onChange={e => setAddedStudentSearchTerm(e.target.value)}
                className='form-control'
              />
            </div>
          )}

          <ul className='list-group' style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {filteredAddedStudents.map(s => (
              <li
                key={s.student_id}
                className='list-group-item d-flex justify-content-between align-items-center'
              >
                {s.firstname} {s.lastname}
                <i
                  className="bi bi-trash text-danger"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleRemoveStudent(s.student_id)}
                />
              </li>
            ))}
            {filteredAddedStudents.length === 0 && addedStudentSearchTerm && (
              <li className='list-group-item text-muted text-center'>Geen resultaten</li>
            )}
            {addedStudents.length === 0 && !addedStudentSearchTerm && (
              <li className='list-group-item text-muted text-center'>
                Nog geen leerlingen toegevoegd
              </li>
            )}
          </ul>
        </div>

        <div className='form-group mb-4'>
          <label>Docenten toevoegen</label>
          {teacherError && <div className="alert alert-danger mt-2">{teacherError}</div>}
          {teacherSuccess && <div className="alert alert-success mt-2">{teacherSuccess}</div>}

          <div className='mt-2'>
            <Select
              isMulti
              options={teacherOptions}
              value={selectedTeacherOptions}
              onChange={(selected) => setSelectedTeacherOptions(selected || [])}
              placeholder='Zoek en selecteer docent(en)...'
              styles={selectStyles}
              noOptionsMessage={() => 'Geen docenten gevonden'}
            />
          </div>

          <div className='mt-2 d-grid'>
            <button
              type="button"
              onClick={handleAddTeachers}
              className='btn btn-primary'
              disabled={selectedTeacherOptions.length === 0 || loadingAddTeacher}
            >
              {loadingAddTeacher
                ? 'Bezig met toevoegen...'
                : `Voeg geselecteerde docent${selectedTeacherOptions.length > 1 ? 'en' : ''} toe`}
            </button>
          </div>

          {teachers.length === 0 && (
            <small className='text-muted mt-2 d-block'>
              Geen beschikbare docenten voor deze locatie.
            </small>
          )}
        </div>

        <div className='mb-4'>
          <label>Toegevoegde Docenten ({addedTeachers.length})</label>
          {addedTeachers.length > 5 && (
            <div className='mb-2'>
              <input
                type='text'
                placeholder='🔍 Filter toegevoegde docenten...'
                value={addedTeacherSearchTerm}
                onChange={e => setAddedTeacherSearchTerm(e.target.value)}
                className='form-control'
              />
            </div>
          )}

          <ul className='list-group' style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {filteredAddedTeachers.map(t => (
              <li
                key={t.docent_id}
                className='list-group-item d-flex justify-content-between align-items-center'
              >
                {t.firstname} {t.lastname}
                <i
                  className="bi bi-trash text-danger"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleRemoveTeacher(t.docent_id)}
                />
              </li>
            ))}
            {filteredAddedTeachers.length === 0 && addedTeacherSearchTerm && (
              <li className='list-group-item text-muted text-center'>Geen resultaten</li>
            )}
            {addedTeachers.length === 0 && !addedTeacherSearchTerm && (
              <li className='list-group-item text-muted text-center'>
                Nog geen docenten toegevoegd
              </li>
            )}
          </ul>
        </div>

        <div className='row'>
          <div className='col-md-4'>
            <button
              type='button'
              onClick={() => navigate('/groups')}
              className='btn btn-secondary'
            >
              Terug
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default EditGroup;